import {
    type ApolloClient,
    type DocumentNode,
    type OperationVariables,
    type ApolloQueryResult,
    ApolloError,
    type NormalizedCacheObject,
} from "@apollo/client";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface QueryServiceOptions<TVariables extends OperationVariables> {
    variables?: TVariables;
    /** Force a network request, bypassing Apollo's cache. Default: false */
    forceNetwork?: boolean;
}

export interface QueryServiceResult<TData> {
    data: TData | null;
    error: ApolloError | null;
}

// ─── Service ─────────────────────────────────────────────────────────────────

/**
 * ApolloQueryService
 *
 * Wraps an ApolloClient instance so that queries can be called as plain
 * async methods — no React context required. Designed to be called from
 * redux-saga via `call()`.
 *
 * Usage in a saga:
 *   const result = yield call([service, service.query], GET_PRODUCTS, { variables })
 */
export class ApolloQueryService {
    // eslint-disable-next-line no-empty-function
    constructor(private client: ApolloClient<NormalizedCacheObject>) {}

    /**
     * Execute a GraphQL query and return a plain result object.
     * Safe to call from a redux-saga generator via `call()`.
     */
    async query<
        TData = unknown,
        TVariables extends OperationVariables = OperationVariables,
    >(
        document: DocumentNode,
        {variables, forceNetwork = false}: QueryServiceOptions<TVariables> = {},
    ): Promise<QueryServiceResult<TData>> {
        try {
            const result: ApolloQueryResult<TData> = await this.client.query<
                TData,
                TVariables
            >({
                query: document,
                variables,
                fetchPolicy: forceNetwork ? "network-only" : "cache-first",
            });

            return {data: result?.data ?? null, error: null};
        } catch (err) {
            return {
                data: null,
                error:
                    err instanceof ApolloError
                        ? err
                        : new ApolloError({errorMessage: String(err)}),
            };
        }
    }

    /** Evict all cached entries for a query, forcing the next call to hit the network. */
    evict(document: DocumentNode): void {
        // Evict by ROOT_QUERY field name derived from the operation
        const operationName = (document.definitions as any[])
            .find((def) => def?.kind === "OperationDefinition")
            ?.selectionSet?.selections?.find((sel: any) => sel.kind === "Field")
            ?.name?.value;
        if (operationName) {
            this.client.cache.evict({fieldName: operationName});
            this.client.cache.gc();
        }
    }

    static of(client: ApolloClient<NormalizedCacheObject>): ApolloQueryService {
        return new ApolloQueryService(client);
    }
}
